/** LeaderboardConfig — auto point rules, celebration behaviors, manual award presets. */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Zap, PartyPopper, Gift, Play, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { previewCelebration, invalidateCelebrationConfig } from "@/lib/celebrate";

const SAMPLE_VARS = { name: "Asmita", points: "+25", reason: "Great work", title: "Design homepage", doc_no: "INV-2026-0042" };

export default function LeaderboardConfig({ workspaceId, onClose }) {
  const [rules, setRules] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!workspaceId) return;
    api.get(`/workspaces/${workspaceId}/points-rules`).then((r) => setRules(r.data || [])).catch(() => {});
    api.get(`/workspaces/${workspaceId}/celebrations/config`).then((r) => setCfg(r.data)).catch(() => {});
  }, [workspaceId]);

  const patchRule = async (id, patch) => {
    try {
      const { data } = await api.patch(`/points-rules/${id}`, patch);
      setRules((rs) => rs.map((r) => (r.id === id ? data : r)));
    } catch (e) { toast.error(apiErrorMessage(e, "Rule update failed")); }
  };

  const setEvent = (key, patch) =>
    setCfg((c) => ({ ...c, events: { ...c.events, [key]: { ...c.events[key], ...patch } } }));

  const saveCelebrations = async () => {
    setBusy(true);
    try {
      await api.put(`/workspaces/${workspaceId}/celebrations/config`, {
        events: cfg.events,
        award_presets: cfg.award_presets,
      });
      invalidateCelebrationConfig();
      toast.success("Celebration settings saved");
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="leaderboard-config-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2"><PartyPopper className="w-4 h-4 text-amber-500" />Rules & celebrations</DialogTitle></DialogHeader>
        <Tabs defaultValue="celebrations">
          <TabsList className="rounded-none">
            <TabsTrigger value="celebrations" className="rounded-none" data-testid="config-tab-celebrations"><PartyPopper className="w-3.5 h-3.5 mr-1.5" />Celebrations</TabsTrigger>
            <TabsTrigger value="rules" className="rounded-none" data-testid="config-tab-rules"><Zap className="w-3.5 h-3.5 mr-1.5" />Auto point rules</TabsTrigger>
            <TabsTrigger value="presets" className="rounded-none" data-testid="config-tab-presets"><Gift className="w-3.5 h-3.5 mr-1.5" />Award presets</TabsTrigger>
          </TabsList>

          <TabsContent value="celebrations" className="mt-4 space-y-3">
            <p className="text-[11px] text-foreground/55">Control what happens on each event — confetti style, sound, and the message that flashes on screen. Use variables like {"{name} {points} {reason} {title} {doc_no}"}.</p>
            {cfg ? cfg.catalog.map((ev) => {
              const e = cfg.events[ev.key];
              return (
                <div key={ev.key} className={`border p-3 space-y-2 transition-opacity ${e.enabled ? "border-border" : "border-border opacity-55"}`} data-testid={`celebration-event-${ev.key}`}>
                  <div className="flex items-center gap-3">
                    <Switch checked={!!e.enabled} onCheckedChange={(v) => setEvent(ev.key, { enabled: v })} data-testid={`celebration-toggle-${ev.key}`} />
                    <div className="text-sm font-semibold flex-1">{ev.label}</div>
                    <Badge variant="outline" className="rounded-none text-[9px]">{"vars: " + ev.vars.map((v) => `{${v}}`).join(" ")}</Badge>
                    <Button size="sm" variant="outline" className="rounded-none h-7 px-2 text-[10px]"
                      onClick={() => previewCelebration(e, SAMPLE_VARS)} data-testid={`celebration-test-${ev.key}`}>
                      <Play className="w-3 h-3 mr-1" />Test
                    </Button>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-2">
                    <Select value={e.confetti} onValueChange={(v) => setEvent(ev.key, { confetti: v })}>
                      <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`celebration-confetti-${ev.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{cfg.confetti_styles.map((s) => <SelectItem key={s} value={s}>Confetti: {s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Select value={e.sound} onValueChange={(v) => setEvent(ev.key, { sound: v })}>
                      <SelectTrigger className="h-8 rounded-none text-xs" data-testid={`celebration-sound-${ev.key}`}><SelectValue /></SelectTrigger>
                      <SelectContent>{cfg.sounds.map((s) => <SelectItem key={s} value={s}>Sound: {s}</SelectItem>)}</SelectContent>
                    </Select>
                    <Input type="number" min={500} max={15000} step={500} value={e.duration_ms}
                      onChange={(ev2) => setEvent(ev.key, { duration_ms: Number(ev2.target.value) })}
                      className="h-8 rounded-none text-xs" title="Flash duration (ms)" data-testid={`celebration-duration-${ev.key}`} />
                  </div>
                  <Input value={e.message} onChange={(ev2) => setEvent(ev.key, { message: ev2.target.value })}
                    className="h-8 rounded-none text-xs" placeholder="Message that flashes on screen" data-testid={`celebration-message-${ev.key}`} />
                </div>
              );
            }) : <div className="text-sm text-foreground/55">Loading…</div>}
          </TabsContent>

          <TabsContent value="rules" className="mt-4 space-y-2">
            <p className="text-[11px] text-foreground/55">Automatic points awarded by the system when these things happen. Toggle off or change the amounts.</p>
            {rules.map((r) => (
              <div key={r.id} className="border border-border p-3 flex items-center gap-3" data-testid={`points-rule-${r.id}`}>
                <Switch checked={!!r.enabled} onCheckedChange={(v) => patchRule(r.id, { enabled: v })} data-testid={`rule-toggle-${r.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold truncate">{r.label}</div>
                  {r.description && <div className="text-[11px] text-foreground/55 truncate">{r.description}</div>}
                </div>
                <Input type="number" value={r.points_delta} className="h-8 w-24 rounded-none font-mono text-xs"
                  onChange={(e) => setRules((rs) => rs.map((x) => (x.id === r.id ? { ...x, points_delta: Number(e.target.value) } : x)))}
                  onBlur={(e) => patchRule(r.id, { points_delta: Number(e.target.value) })}
                  data-testid={`rule-points-${r.id}`} />
                <span className="text-[10px] text-foreground/50">pts</span>
              </div>
            ))}
            {rules.length === 0 && <div className="text-sm text-foreground/55">No auto rules found.</div>}
          </TabsContent>

          <TabsContent value="presets" className="mt-4 space-y-2">
            <p className="text-[11px] text-foreground/55">Quick-pick award behaviors shown in the Award points dialog.</p>
            {cfg?.award_presets?.map((p, i) => (
              <div key={p.id || i} className="flex items-center gap-2" data-testid={`award-preset-row-${i}`}>
                <Input value={p.emoji || ""} onChange={(e) => setCfg((c) => { const a = [...c.award_presets]; a[i] = { ...a[i], emoji: e.target.value }; return { ...c, award_presets: a }; })} className="h-8 w-14 rounded-none text-center" placeholder="🏆" />
                <Input value={p.label} onChange={(e) => setCfg((c) => { const a = [...c.award_presets]; a[i] = { ...a[i], label: e.target.value }; return { ...c, award_presets: a }; })} className="h-8 flex-1 rounded-none text-xs" placeholder="Label" />
                <Input type="number" value={p.points} onChange={(e) => setCfg((c) => { const a = [...c.award_presets]; a[i] = { ...a[i], points: Number(e.target.value) }; return { ...c, award_presets: a }; })} className="h-8 w-20 rounded-none font-mono text-xs" />
                <Button size="sm" variant="ghost" className="rounded-none text-red-500 h-8" onClick={() => setCfg((c) => ({ ...c, award_presets: c.award_presets.filter((_, j) => j !== i) }))} data-testid={`award-preset-del-${i}`}><Trash2 className="w-3.5 h-3.5" /></Button>
              </div>
            ))}
            <Button size="sm" variant="outline" className="rounded-none" onClick={() => setCfg((c) => ({ ...c, award_presets: [...(c.award_presets || []), { label: "", points: 10, emoji: "🏅" }] }))} data-testid="award-preset-add">
              <Plus className="w-3.5 h-3.5 mr-1" /> Add preset
            </Button>
          </TabsContent>
        </Tabs>
        <DialogFooter>
          <Button variant="outline" className="rounded-none" onClick={onClose}>Close</Button>
          <Button className="rounded-none" onClick={saveCelebrations} disabled={busy || !cfg} data-testid="celebration-save-btn">{busy ? "Saving…" : "Save celebrations & presets"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
